import { unexpectedValue } from '../Types';
import { ProductId } from 'Utility/Product';
import { PurchaseStatus } from './TrackingHelpers/PurchaseStatus';

export enum FBTrackingEvent {
    // FBA standard / built-in events

    /**
     * @see README.md#FB_Standard_events
     * 16.06.2020
     */
    CompleteTutorial = 'fb_mobile_tutorial_completion',

    /**
     * Intentionally misused in order to have it as a "standard" event
     * @see README.md#FB_Standard_events
     * 04.08.2020
     */
    Adwhales_24h_5_ads = 'fb_mobile_complete_registration',
    /**
     * Intentionally misused in order to have it as a "standard" event
     * @see README.md#FB_Standard_events
     * 04.08.2020
     */
    Adwhales_24h_10_ads = 'fb_mobile_add_to_wishlist',
    /**
     * Intentionally misused in order to have it as a "standard" event
     * @see README.md#FB_Standard_events
     * 04.08.2020
     */
    HeavyUsers_24h_10_lvl_complete = 'fb_mobile_level_achieved',
    /**
     * Intentionally misused in order to have it as a "standard" event
     * @see README.md#FB_Standard_events
     * 04.08.2020
     */
    HeavyUsers_24h_20_lvl_complete = 'fb_mobile_achievement_unlocked',

    // Main Events

    FTUE_00_FBInit = 'fbInit',
    FTUE_01_GbcInit = 'gbcInit',
    FTUE_02_LoadingCompleted = 'loadingComplete',
    FTUE_03_GameplayShown = 'gameShown',
    FTUE_04_FirstCardClear = 'firstCardCleared',
    FTUE_05_Level1FinishPopupShow = 'level1LevelCompleteShow',
    FTUE_06_Level1Continue = 'level1Continue',
    FTUE_07_Level2Play = 'level2Play',
    FTUE_08_Level2Loaded = 'level2Loaded',
    FTUE_09_Level2FinishPopupShow = 'level2LevelCompleteShow',
    FTUE_10_Level3Play = 'level3Play',
    FTUE_11_Level3Loaded = 'level3Loaded',
    FTUE_12_Level3FinishPopupShow = 'level3LevelCompleteShow',
    FTUE_13_CropsUnlockedShow = 'cropUnlockShow',
    FTUE_14_HarvestButtonClicked = 'harvestButtonClicked',
    FTUE_15_HarvestPopupShow = 'harvestShow',
    FTUE_16_CollectButtonClicked = 'harvestCollectClicked',

    // Ad Events
    VideoAdWatchGetCoins_NotEnoughCoins = 'watchAd_NotEnoughCoins',
    VideoAdWatchGetCoins_CoinBar = 'watchAd_coinBar',
    VideoAdWatchToPlay = 'VideoAdWatchToPlay',
    Design_RewardedAdRequested = 'adRequested_Rewarded',
    Design_RewardedAdDisplayed = 'adDisplayed_Rewarded',
    adRequestFailed_Rewarded = 'adRequestFailed_Rewarded', // After rewarded ad is requested but cannot be filled with an ad.
    Design_InterstitialAdRequested = 'adRequested_Interstitial',
    Design_InterstitialAdDisplayed = 'adDisplayed_Interstitial',
    VideoAdWatchGetCoins_FreeCoinsIcon = 'VideoAdWatchGetCoins_FreeCoinsIcon',

    // Coin Sink
    TapOnBuyFiveCards = 'TapOnBuyFiveCards', // Successfully Purchasing +5 cards
    TapOnUndoMove = 'TapOnUndoMove', // Successfully Purchasing Undo Move Booster
    TapOnJokerCard = 'TapOnJokerCard', // Successfully Purchasing Joker Booster
    BuyLifeCoins = 'BuyLifeCoins', // Successfully Purchasing Life

    TapOnSlingShot = 'TapOnSlingshot', // Successfully Purchasing Slingshot Booster
    TapOnCanister = 'TapOnCanister', // Successfully Purchasing Canister Booster
    TapOnElectricFan = 'TapOnElectricFan', // Successfully Purchasing Electric Fan Booster
    TapOnPlay = 'TapOnPlay', // Successfully Purchasing Level Entry Fee
    TapOnReplay = 'TapOnReplay', // Successfully Purchasing Level Replay Entry Fee

    // Bot Opt-In
    Bot_show = 'Bot_show', // User Taps on Bot opt-in icon and window is shown.
    Bot_deny = 'Bot_deny', // User taps on "deny" on facebook bot UI window
    Bot_confirm = 'Bot_confirm', // User taps on "Turn on" on facebook bot UI window

    // Social Hub, Top List & Friend Level Nodes
    TopList_Coins_Show = 'TopList_Coins_Show', // User taps on Coin icon
    TopList_Coins_Claim = 'TopList_Coins_Claim', // User confirms context switch (FB UI) and receives coins for gift
    Top_List_Coins_Entry = 'Top_List_Coins_Entry', // Friend enters the game via sent custom message
    SagaMap_Coins_Show = 'SagaMap_Coins_Show', // User taps on Friends Level Node
    SagaMap_Coins_Claim = 'SagaMap_Coins_Claim', // User confirms context switch (FB UI) and receives coins for gift
    SagaMap_Coins_Entry = 'SagaMap_Coins_Entry', // Friend enters the game via sent custom message
    SocialHub_Invite = 'SocialHub_Invite', // User taps on Invite Friends button at Friends Popup
    SocialHub_Invite_Entry = 'SocialHub_Invite_Entry', // Friend enters the game via invite message
    TopList_Invite = 'TopList_Invite', // User taps on Invite Friends button at Top List Popup Section
    TopList_Invite_Entry = 'TopList_Invite_Entry', // Friend enters the game via invite message
    Coin_Request = 'Coin_Request', //User requests coins from their friend
    Coin_Request_Entry = 'Coin_Request_Entry', // Friend enters the game via sent custom message and sends coins
    Coin_Send = 'Coin_Send', // User sends coins to their friend
    Coin_Send_Entry = 'Coin_Send_Entry', //Friend enters the game via sent custom message and receives coins
    Coins_Claim = 'Coins_Claim', // User collects coins from friend and sends a thank message (iOS Users)
    Coins_Claim_Entry = 'Coins_Claim_Entry', // Friend enters the game via sent custom message (iOS Users)

    // Level Helper
    Helper_Show = 'Helper_Show', // Helper friend feature appears
    Helper_Accept = 'Helper_Accept', // Player taps accept help on helper icon
    Helper_Confirm = 'Helper_Confirm', // Player confirms context switch and receives cards
    Helper_Entry = 'Helper_Entry', // Friend enters the game via sent custom message

    // Share
    NewField_Share = 'NewField_Share', // User taps on Share button at New Field Sharing Popup
    Three_Stars_Share = 'Three_Stars_Share', // User taps on Share button at 3Stars Sharing Popup
    Crop_Share = 'Crop_Share',
    Pet_Unlock_Share = 'Pet_Unlock_Share',
    Pet_Jackpot_Share = 'Pet_Jackpot_Share',
    Hard_Level_Share = 'Hard_Level_Share',
    Farm_Share = 'Farm_Share',

    // Level Progression
    Level4PreLevelButtonPlay = 'Level4PreLevelButtonPlay', // User tap on level 4 play button (pre level UI)
    Gamefield4IsShow = 'Gamefield4IsShow', // Level 4 loads
    Level04FinishPopupShow = 'Level04FinishPopupShow', // Level 4 win screen is shown
    Level5PreLevelButtonPlay = 'Level5PreLevelButtonPlay', // User tap on level 5 play button (pre level UI)
    Gamefield5IsShow = 'Gamefield5IsShow', // Level 5 loads
    Level05FinishPopupShow = 'Level05FinishPopupShow', // Level 5 win screen is shown
    Level6PreLevelButtonPlay = 'Level6PreLevelButtonPlay', // User tap on level 6 play button (pre level UI)
    Gamefield6IsShow = 'Gamefield6IsShow', // Level 6 loads
    Level06FinishPopupShow = 'Level06FinishPopupShow', // Level 6 win screen is shown
    Level7PreLevelButtonPlay = 'Level7PreLevelButtonPlay', // User tap on level 7 play button (pre level UI)
    Gamefield7IsShow = 'Gamefield7IsShow', // Level 7 loads
    Level07FinishPopupShow = 'Level07FinishPopupShow', // Level 7 win screen is shown
    levelComplete_10 = 'levelComplete_10', // Level 10 completed
    levelComplete_20 = 'levelComplete_20', // Level 20 completed
    levelComplete_30 = 'levelComplete_30', // Level 30 completed
    levelComplete_40 = 'levelComplete_40', // Level 40 completed
    levelComplete_50 = 'levelComplete_50', // Level 50 completed
    levelComplete_60 = 'levelComplete_60', // Level 60 completed
    levelComplete_70 = 'levelComplete_70', // Level 70 completed
    levelComplete_80 = 'levelComplete_80', // Level 80 completed
    levelComplete_90 = 'levelComplete_90', // Level 90 completed
    levelComplete_100 = 'levelComplete_100', // Level 100 completed
    levelComplete_150 = 'levelComplete_150', // Level 150 completed
    levelComplete_200 = 'levelComplete_200', // Level 200 completed

    // Cross Promo
    X_PROMO = 'Entries_xPromo',
    X_PROMO_NEW = 'XPromo_new_user',
    X_PROMO_OLD = 'XPromo_old_user',

    OpenShop_CoinBar = 'OpenShop_CoinBar', // User opens shop from coin bar
    OpenShop_ShopIcon = 'OpenShop_ShopIcon', // User opens shop from shop icon on saga map
    OpenShop_NotEnoughCoins = 'OpenShop_NotEnoughCoins', // User opens shop from Not Enough Coins popup

    Marketing_Campaign = 'marketing_campaign', // User was received by the app through marketing campaigns

    Returning_User = 'returning_user',

    Heavy_User = 'heavy_user',

    SESSION_5_3DAYS = '5plusSessions_within3d',
    SESSION_8_5DAYS = '8plusSessions_within5d',
    SESSION_11_7DAYS = '11plusSessions_within7d',

    LVL_20_3DAYS = '20plusLvl_within3d',
    LVL_40_5DAYS = '40plusLvl_within5d',
    LVL_60_7DAYS = '60plusLvl_within7d',

    AD_10_3DAYS = '10plusRwAds_within3d',
    AD_20_5DAYS = '20plusRwAds_within5d',
    AD_30_7DAYS = '30plusRwAds_within7d',

    SESSION_5_1DAY = '5plusSessions_within1d',
    SESSION_5_2DAYS = '5plusSessions_within2d',
    SESSION_5_5DAYS = '5plusSessions_within5d',
    SESSION_5_7DAYS = '5plusSessions_within7d',

    SESSION_4_1DAY = '4plusSessions_within1d',
    SESSION_4_2DAYS = '4plusSessions_within2d',
    SESSION_4_3DAYS = '4plusSessions_within3d',

    SESSION_3_1DAY = '3plusSessions_within1d',
    SESSION_3_2DAYS = '3plusSessions_within2d',
    SESSION_3_3DAYS = '3plusSessions_within3d',

    SESSION_2_1DAY = '2plusSessions_within1d',
    SESSION_2_2DAYS = '2plusSessions_within2d',
    SESSION_2_3DAYS = '2plusSessions_within3d',

    LVL_20_1DAY = '20plusLevels_within1d',
    LVL_20_2DAYS = '20plusLevels_within2d',
    LVL_20_5DAYS = '20plusLevels_within5d',
    LVL_20_7DAYS = '20plusLevels_within7d',

    LVL_10_1DAY = '10plusLevels_within1d',
    LVL_10_2DAYS = '10plusLevels_within2d',
    LVL_10_3DAYS = '10plusLevels_within3d',

    LVL_15_1DAY = '15plusLevels_within1d',
    LVL_15_2DAYS = '15plusLevels_within2d',
    LVL_15_3DAYS = '15plusLevels_within3d',

    /**
     * !!!!!!!!!!!!!!!!!
     * !!!! WARNING !!!!
     * !!!!!!!!!!!!!!!!!
     *
     * There are also IAP tracking events, which are meant to be part of this enum.
     * However to avoid code duplication they are generated on the fly via `getIAPEventName`
     */
    LEVEL_01_COMPLETE = "level_01_complete",
    LEVEL_02_COMPLETE = "level_02_complete",
    LEVEL_03_COMPLETE = "level_03_complete",
    LEVEL_04_COMPLETE = "level_04_complete",
    LEVEL_05_COMPLETE = "level_05_complete",
    LEVEL_06_COMPLETE = "level_06_complete",
    LEVEL_07_COMPLETE = "level_07_complete",
    LEVEL_08_COMPLETE = "level_08_complete",
    LEVEL_09_COMPLETE = "level_09_complete",
    LEVEL_10_COMPLETE = "level_10_complete",
    LEVEL_20_COMPLETE = "level_20_complete",
    LEVEL_30_COMPLETE = "level_30_complete",
    LEVEL_40_COMPLETE = "level_40_complete",
    LEVEL_50_COMPLETE = "level_50_complete",
    LEVEL_60_COMPLETE = "level_60_complete",
    LEVEL_70_COMPLETE = "level_70_complete",
    LEVEL_80_COMPLETE = "level_80_complete",
    LEVEL_90_COMPLETE = "level_90_complete",
    LEVEL_100_COMPLETE = "level_100_complete",
    LEVEL_200_COMPLETE = "level_200_complete",
    LEVEL_300_COMPLETE = "level_300_complete",
    LEVEL_400_COMPLETE = "level_400_complete",
    LEVEL_500_COMPLETE = "level_500_complete",
    LEVEL_600_COMPLETE = "level_600_complete",
    LEVEL_700_COMPLETE = "level_700_complete",
    LEVEL_800_COMPLETE = "level_800_complete",
    LEVEL_900_COMPLETE = "level_900_complete",
    LEVEL_1000_COMPLETE = "level_1000_complete",

    AD_WHALES_24h_5_ADS = "adwhales_24h_5_ads",
    AD_WHALES_24h_10_ADS = "adwhales_24h_10_ads",
    HEAVY_USERS_24h_10_lvl_complete = "heavyUsers_24h_10_lvl_complete",
    HEAVY_USERS_24h_20_lvl_complete = "heavyUsers_24h_20_lvl_complete",

    /*
     * These IAP product id's should be the same in
     * Google Play and Apple App store.
     */
    ONE_MILLION_COINS = "1000000coins_v2_44.99",
    TEN_THOUSAND_COINS = "10000coins_0.99",
    ONE_HUNDRED_TWENTY_THOUSAND_COINS = "120000coins_8.99",
    SIXTY_THOUSAND_COINS = "60000coins_4.99",
    JOKER_DEAL_v2 = "jokerdeal_v2_19.99",
    SPECIAL_OFFER = "specialoffer_v1_9.99",
    STARTER_PACK_v3 = "starterpack_v3_1.99",
    SUNDAY_OFFER_V2 = "sunday_offer_v2_34.99",
    SUNDAY_OFFER = "sunday_offer_v2_34.99",

    INACTIVITY_NOTIFICATION_Sent = 'INACTIVITY_NOTIFICATION_Sent',
    INACTIVITY_NOTIFICATION_Received = 'NACTIVITY_NOTIFICATION_Received',
    INACTIVITY_NOTIFICATION_Open = 'INACTIVITY_NOTIFICATION_Open',
}

export function getIAPEventName(
    productId: ProductId,
    purchaseStatus: PurchaseStatus
) {
    switch (purchaseStatus) {
        case PurchaseStatus.START:
        case PurchaseStatus.FAIL:
        case PurchaseStatus.SUCCESS:
        case PurchaseStatus.TEST:
        case PurchaseStatus.ERROR:
            return productIdToTracking(productId, purchaseStatus.toString());
        default:
            unexpectedValue(purchaseStatus, false);
    }
}

function productIdToTracking(
    productId: ProductId,
    purchaseStatus: string
): string {
    // If it's all numbers (like '001', '002' etc) - turn it into 'Product001', 'Product002' etc
    const prefix = /^\d+$/.test(productId) ? 'Product' : '';
    // turn stuff like 'starterpack_v2_0.99' into 'starterpack_v2_0_99'
    const normalizedProductId = productId.replace('.', '_');
    // e.g. 'Product001_Succeess', 'starterpack_v2_0_99_Start' etc
    return `${prefix}${normalizedProductId}_${purchaseStatus}`;
}
